/** Healthy cadence for the streaming-session poll. */
const BASE_DELAY_MS = 3_000;
/** Slow enough to stop feeding a rate limiter, fast enough to notice recovery. */
const MAX_DELAY_MS = 30_000;
/** Cadence while the only streaming conversation is the one this tab is writing. */
const IDLE_DELAY_MS = 15_000;

/**
 * How long to wait before the next streaming-session tick.
 *
 * The tick is the only thing that clears a stale `isStreaming` flag, and the
 * flag is the only thing keeping the tick alive. Retrying a rate-limited tick
 * at the healthy cadence therefore deadlocks: the loop keeps the limiter angry,
 * and the limiter keeps the loop from clearing the flag that started it.
 */
export function streamingPollDelayMs(
  consecutiveFailures: number,
  options: { watchingOnlyLocalStreams?: boolean; pageHidden?: boolean } = {},
): number {
  if (options.pageHidden) return MAX_DELAY_MS;
  if (consecutiveFailures > 0) {
    return Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** consecutiveFailures);
  }

  // Nothing to watch but our own stream.
  //
  // The tick exists to notice conversations streaming in *another* tab, and it
  // deliberately skips hydrating any session this tab is generating — that one
  // is already being fed by its own SSE stream and the local copy is ahead of
  // the server's. So while the only streaming session is ours, every three
  // seconds buys nothing: a ten-minute generation spent roughly two hundred
  // requests on it, and the 2026-09-07 walkthrough measured 18 of 23 requests
  // in one window going to this endpoint.
  //
  // It still has to run: the flag it clears is what stops the loop, and another
  // tab can start streaming at any point. It just does not have to run at the
  // cadence used for watching someone else.
  if (options.watchingOnlyLocalStreams) return IDLE_DELAY_MS;

  return BASE_DELAY_MS;
}
